import { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import MetricCard from '../components/MetricCard';
import { getDashboardStats } from '../services/dashboardService';
import '../components/DataPage.css';
import './Dashboard.css';

const PAGE_SIZE = 10;
const WINDOW_HOURS = 24;

const tableCustomStyles = {
  table: {
    style: {
      backgroundColor: 'transparent',
    },
  },
  headRow: {
    style: {
      backgroundColor: 'var(--color-surface)',
      borderBottom: '1px solid var(--color-border)',
      minHeight: '48px',
    },
  },
  headCells: {
    style: {
      color: 'var(--color-text-muted)',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  rows: {
    style: {
      backgroundColor: 'transparent',
      borderBottom: '1px solid var(--color-border)',
      minHeight: '48px',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'var(--color-accent-soft)',
    },
  },
  cells: {
    style: {
      color: 'var(--color-text)',
      fontSize: '0.88rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  pagination: {
    style: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-muted)',
      borderTop: '1px solid var(--color-border)',
      fontSize: '0.82rem',
      minHeight: '52px',
    },
  },
  noData: {
    style: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-muted)',
    },
  },
  progress: {
    style: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-muted)',
    },
  },
};

function formatCount(value) {
  if (value === null || value === undefined) {
    return '0';
  }
  return Number(value).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadStats() {
      setLoading(true);
      try {
        const data = await getDashboardStats();
        if (isMounted) {
          setStats(data);
        }
      } catch (err) {
        if (isMounted) {
          toast.error(err.message);
          setStats(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      isMounted = false;
    };
  }, []);

  const scheduledJobs = stats?.scheduledJobs ?? [];

  const metrics = [
    {
      title: 'Sends left',
      value: loading ? '...' : formatCount(stats?.totalAvailableEmailQuota),
      subtext: `Sends left across all senders (last ${WINDOW_HOURS} hours)`,
      highlight: true,
    },
    {
      title: 'Waiting groups',
      value: loading ? '...' : formatCount(stats?.totalPendingBatches),
      subtext: 'Groups with emails waiting to send',
    },
    {
      title: 'Total Vessels',
      value: loading ? '...' : formatCount(stats?.totalVessels),
      subtext: 'Vessels in your list',
    },
    {
      title: 'Emails Sent (24h)',
      value: loading ? '...' : formatCount(stats?.totalEmailsSentLast24Hours),
      subtext: 'Sent successfully in the last 24 hours',
      highlight: true,
    },
  ];

  const columns = useMemo(
    () => [
      {
        name: 'Group ID',
        selector: (row) => row.batchId,
        sortable: true,
        cell: (row) => (
          <div className="data-page__cell-truncate">{row.batchId ?? '—'}</div>
        ),
      },
      {
        name: 'Subject',
        selector: (row) => row.subject,
        sortable: true,
        cell: (row) => (
          <div className="data-page__cell-truncate">{row.subject ?? '—'}</div>
        ),
      },
      {
        name: 'Sender',
        selector: (row) => row.senderEmail,
        sortable: true,
        cell: (row) => (
          <div className="data-page__cell-truncate">{row.senderEmail ?? '—'}</div>
        ),
      },
      {
        name: 'Total',
        selector: (row) => row.total,
        sortable: true,
        cell: (row) => formatCount(row.total),
      },
      {
        name: 'Queued',
        selector: (row) => row.queued,
        sortable: true,
        cell: (row) => formatCount(row.queued),
      },
      {
        name: 'Status',
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => (
          <span className={`data-page__status data-page__status--${row.status}`}>
            {row.status ?? '—'}
          </span>
        ),
      },
      {
        name: 'Scheduled For',
        selector: (row) => row.scheduledFor,
        sortable: true,
        cell: (row) => formatDate(row.scheduledFor),
      },
      {
        name: 'Created At',
        selector: (row) => row.createdAt,
        sortable: true,
        cell: (row) => formatDate(row.createdAt),
      },
    ],
    []
  );

  const paginationOptions = useMemo(
    () => ({
      rangeSeparatorText: 'of',
      rowsPerPageText: '',
    }),
    []
  );

  return (
    <>
      <section className="dashboard__intro">
        <h2>Dashboard</h2>
        <p className="dashboard__subtitle">A quick look at how things are going.</p>
      </section>

      <section className="dashboard__metrics" aria-label="Summary">
        {metrics.map((metric) => (
          <MetricCard key={metric.title} {...metric} />
        ))}
      </section>

      <section className="dashboard__jobs" aria-label="Scheduled emails">
        <div className="dashboard__jobs-header">
          <h3>Scheduled Emails</h3>
          <p>Email groups waiting to send.</p>
        </div>

        <div className="data-page__table-wrap data-page__rdt">
          <DataTable
            columns={columns}
            data={scheduledJobs}
            keyField="batchId"
            progressPending={loading}
            pagination
            paginationPerPage={PAGE_SIZE}
            paginationRowsPerPageOptions={[10, 25, 50]}
            paginationComponentOptions={paginationOptions}
            customStyles={tableCustomStyles}
            noDataComponent={
              <div className="data-page__empty">
                {loading ? 'Loading...' : 'Nothing waiting to send.'}
              </div>
            }
            persistTableHead
            highlightOnHover
            responsive
          />
        </div>
      </section>
    </>
  );
}

export default Dashboard;
